/**
 * A rating or a share as a row of segments — "better a one-to-five than a
 * random number".
 *
 * Vesper shows most judgements this way: a strength of 1–5 with a word beside
 * it, a share of 9 in 10, a specification on a 10-step scale. The exact
 * number, when it matters, sits small beside the bar; the bar is what the eye
 * reads.
 *
 *   steps    5 (a rating), 10 (a share, a specification), 4 (a count
 *            toward four — the quarters a year's figures wait for — or a
 *            scale of four levels, such as how well your airline knows a
 *            market), or one for each of a few things counted: a team's
 *            votes, a step per player who voted
 *   tone     accent    purple — ratings, one of purple's highlight moments
 *            progress  gold — forward progress, or a share that needs you
 *            neutral   the soft ink — a share that is only a fact, with no
 *                      judgement in it: one line's share of a whole
 *            hue       a colour known only at runtime (`hue`); it must read at
 *                      3:1 against the empty segments, as every rival colour
 *                      from team-colors does
 *   size     md · sm
 *   stretch  fill the row, so bars on neighbouring rows share one length and
 *            their scales compare
 *   onNight  for a bar on a night board: the empty segments turn lavender, and
 *            the accent tone takes the board's light purple
 *   adding   steps a change would fill, hatched in the tone after the filled
 *            ones — what the decision beside the bar adds, before it is made
 *
 * It is a meter to assistive technology: `label` names it, and `word` — when
 * there is one — is read as its value.
 */

import type { CSSProperties, HTMLAttributes } from 'react';

import styles from './segment-bar.module.css';

const TONE = {
  accent: styles.accent,
  progress: styles.progress,
  neutral: styles.neutral,
} as const;

const SIZE = {
  md: '',
  sm: styles.sm,
} as const;

type SegmentBarProps = Omit<HTMLAttributes<HTMLSpanElement>, 'role' | 'style' | 'children'> & {
  /** Filled segments, from 0 to `steps`. Rounded, and clamped to the range. */
  value: number;
  /** 5, 10 or 4 for a scale; for a count, one for each thing counted. */
  steps?: number;
  size?: keyof typeof SIZE;
  stretch?: boolean;
  onNight?: boolean;
  /** Names the meter for assistive technology: "Strength", "Range". */
  label: string;
  /** The rating in a word, shown beside the bar: "Weak", "Dominant". */
  word?: string;
  /** Steps a change would fill, hatched after the filled ones. */
  adding?: number;
} & (
  | { tone?: keyof typeof TONE; hue?: never }
  /** `hue`: any CSS colour — `#rrggbb` from the data, or a `var()` a card sets, such as its field's light. */
  | { tone: 'hue'; hue: string }
);

export function SegmentBar({
  value,
  steps = 5,
  tone = 'accent',
  hue,
  size = 'md',
  stretch = false,
  onNight = false,
  label,
  word,
  adding = 0,
  className = '',
  ...rest
}: SegmentBarProps) {
  const filled = Math.max(0, Math.min(steps, Math.round(value)));
  const added = Math.max(0, Math.min(steps - filled, Math.round(adding)));
  const toneClass = tone === 'hue' ? styles.hue : TONE[tone];
  const hueStyle = tone === 'hue' ? ({ '--hue': hue } as CSSProperties) : undefined;
  return (
    <span
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={steps}
      aria-valuenow={filled}
      aria-valuetext={word}
      style={hueStyle}
      className={[
        styles.segmentBar,
        toneClass,
        SIZE[size],
        steps >= 10 ? styles.ten : '',
        stretch ? styles.stretch : '',
        onNight ? styles.onNight : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <span className={styles.track} aria-hidden="true">
        {Array.from({ length: steps }, (_, i) => (
          <i key={i} className={i < filled ? styles.on : i < filled + added ? styles.adding : undefined} />
        ))}
      </span>
      {word && <span className={styles.word}>{word}</span>}
    </span>
  );
}
